/**
 * PersonaAssignmentsTab
 *
 * Shows every workflow that has an AI node, with the bot persona assigned to each.
 * Lets admins quickly see which persona drives which workflow and navigate to re-link.
 */
import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { BotProfile } from "./types";

interface WorkflowListItem {
  id: number;
  name: string;
  enabled: boolean;
  workflowStatus?: string;
  triggerType?: string;
}

interface ConnectedWorkflow extends WorkflowListItem {
  botProfileId?: number;
}

interface Props {
  botProfiles: BotProfile[];
}

export default function PersonaAssignmentsTab({ botProfiles }: Props) {
  const api = useApi();
  const [assignments, setAssignments] = useState<ConnectedWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Single batch endpoint — scans NodesBlob once for all personas instead of N requests
        const [wfResp, connResp] = await Promise.all([
          api.messaging.get("Workflows/List"),
          api.messaging.get("BotProfiles/workflow-connections"),
        ]);

        const allWorkflows: WorkflowListItem[] = Array.isArray(await wfResp.clone().json())
          ? await wfResp.json()
          : [];

        const connections: { workflowId: number; botProfileId: number }[] =
          Array.isArray(await connResp.clone().json()) ? await connResp.json() : [];

        const connectedMap = new Map<number, number>(); // workflowId → botProfileId
        for (const c of connections) connectedMap.set(c.workflowId, c.botProfileId);

        const rows: ConnectedWorkflow[] = allWorkflows
          .filter((wf) => !("isTemplate" in wf && (wf as any).isTemplate))
          .map((wf) => ({ ...wf, botProfileId: connectedMap.get(wf.id) }));

        setAssignments(rows);
      } catch { /* ignore */ } finally {
        setLoading(false);
      }
    }
    load();
  }, [api.messaging, botProfiles]);

  const profileMap = new Map(botProfiles.map((p) => [p.id, p]));
  const defaultProfile = botProfiles.find((p) => p.isDefault) ?? botProfiles[0];

  const filtered = assignments.filter((a) =>
    !search || a.name.toLowerCase().includes(search.toLowerCase())
  );

  // Only show workflows that have explicit persona assignments (botProfileId set)
  const assignedRows = filtered.filter((a) => a.botProfileId);
  const defaultRows = filtered.filter((a) => !a.botProfileId);

  return (
    <div className="space-y-5">
      {/* Search */}
      <div className="relative">
        <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs" />
        <input
          type="text"
          placeholder="Search workflows…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-400 gap-2">
          <i className="fa fa-circle-o-notch fa-spin" />
          <span className="text-sm">Loading assignments…</span>
        </div>
      ) : (
        <>
          {/* Explicitly assigned workflows */}
          {assignedRows.length > 0 && (
            <section>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Custom persona assigned
              </h3>
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {assignedRows.map((wf) => {
                  const persona = profileMap.get(wf.botProfileId!);
                  return (
                    <WorkflowRow
                      key={wf.id}
                      workflow={wf}
                      persona={persona}
                      label="custom"
                    />
                  );
                })}
              </div>
            </section>
          )}

          {/* Default persona workflows */}
          {defaultRows.length > 0 && (
            <section>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Using site default
                {defaultProfile && (
                  <span className="ml-2 font-normal normal-case text-slate-400">({defaultProfile.name})</span>
                )}
              </h3>
              <div className="rounded-xl border border-slate-100 divide-y divide-slate-50 overflow-hidden bg-slate-50/50">
                {defaultRows.map((wf) => (
                  <WorkflowRow
                    key={wf.id}
                    workflow={wf}
                    persona={defaultProfile}
                    label="default"
                  />
                ))}
              </div>
            </section>
          )}

          {assignedRows.length === 0 && defaultRows.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <i className="fa fa-sitemap text-3xl mb-3 block" />
              <p className="text-sm">No workflows found{search ? ` matching "${search}"` : ""}.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WorkflowRow({
  workflow,
  persona,
  label,
}: {
  workflow: ConnectedWorkflow;
  persona?: BotProfile;
  label: "custom" | "default";
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50 transition-colors">
      {/* Status dot */}
      <span
        title={workflow.enabled ? "Active" : "Disabled"}
        className={`w-2 h-2 rounded-full flex-shrink-0 ${workflow.enabled ? "bg-green-400" : "bg-slate-300"}`}
      />

      {/* Workflow name */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700 truncate">{workflow.name}</p>
        {workflow.triggerType && (
          <p className="text-xs text-slate-400 capitalize">{workflow.triggerType}</p>
        )}
      </div>

      {/* Persona chip */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {persona?.picture && (
          <img src={persona.picture} alt="" className="w-5 h-5 rounded-full border border-white shadow-sm" />
        )}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          label === "custom"
            ? "bg-cornflower-blue-50 text-cornflower-blue-700 border border-cornflower-blue-200"
            : "bg-slate-100 text-slate-500"
        }`}>
          {persona?.name ?? "Default"}
        </span>
      </div>

      {/* Edit link */}
      <a
        href={`/workflows/${workflow.id}`}
        className="text-xs text-cornflower-blue-500 hover:text-cornflower-blue-700 hover:underline flex-shrink-0 ml-1"
        title="Open workflow to change persona"
      >
        Change →
      </a>
    </div>
  );
}
